import React, { Fragment, useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { ChatBubbleLeftRightIcon, UserGroupIcon, UserIcon } from '@heroicons/react/24/outline';
import {
  WorkspaceView,
  createAllView,
  createMyView,
  createGroupView,
} from '../../../types/workspaceView';
import { SupportGroupStats } from '../../../types/supportGroup';
import { supportGroupService } from '../../../services/supportGroupService';
import { WorkspaceThemeClasses } from './workspaceTheme';

interface WorkspaceViewSelectorProps {
  theme: WorkspaceThemeClasses;
  activeView: WorkspaceView;
  onViewChange: (view: WorkspaceView) => void;
}

type OptionKey = 'all' | 'mine' | `group-${string}`;

const optionKeyFor = (view: WorkspaceView): OptionKey => {
  if (view.type === 'group' && view.supportGroupId)
  {
    return `group-${view.supportGroupId}`;
  }

  return view.type === 'mine' ? 'mine' : 'all';
};

const buttonLabelFor = (view: WorkspaceView): string => {
  if (view.type === 'all')
  {
    return 'Ver todas as conversas';
  }

  if (view.type === 'mine')
  {
    return 'Visualização: Minhas conversas';
  }

  const groupName = view.label.replace(/^Grupo:\s*/i, '');
  return `Visualização: ${groupName}`;
};

export const WorkspaceViewSelector: React.FC<WorkspaceViewSelectorProps> = ({
  theme,
  activeView,
  onViewChange,
}) => {
  const [groups, setGroups] = useState<SupportGroupStats[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadGroups = async () => {
      try
      {
        const data = await supportGroupService.getStats();
        setGroups(data.filter((group) => group.isActive));
      }
      catch
      {
        setGroups([]);
      }
      finally
      {
        setLoading(false);
      }
    };

    void loadGroups();
  }, []);

  const handleSelect = (key: OptionKey) => {
    if (key === 'all')
    {
      onViewChange(createAllView());
      return;
    }

    if (key === 'mine')
    {
      onViewChange(createMyView());
      return;
    }

    const supportGroupId = key.slice('group-'.length);
    const group = groups.find((item) => item.supportGroupId === supportGroupId);
    if (group)
    {
      onViewChange(createGroupView(group.supportGroupId, group.name));
    }
  };

  const activeKey = optionKeyFor(activeView);
  const buttonLabel = buttonLabelFor(activeView);

  return (
    <div className="min-w-0">
      <Listbox value={activeKey} onChange={handleSelect} disabled={loading}>
        <div className="relative">
          <Listbox.Button
            aria-label="Filtrar por visualização"
            className={`relative box-border flex h-9 max-h-9 min-h-9 w-full cursor-pointer items-center overflow-hidden rounded-md border px-2 py-0 pr-7 text-left text-[11px] leading-none transition-colors focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 ${theme.input}`}
          >
            <span className="flex min-w-0 items-center gap-1.5">
              {activeView.type === 'mine' ? (
                <UserIcon className={`h-3.5 w-3.5 flex-shrink-0 ${theme.faint}`} aria-hidden="true" />
              ) : activeView.type === 'group' ? (
                <UserGroupIcon className={`h-3.5 w-3.5 flex-shrink-0 ${theme.faint}`} aria-hidden="true" />
              ) : (
                <ChatBubbleLeftRightIcon className={`h-3.5 w-3.5 flex-shrink-0 ${theme.faint}`} aria-hidden="true" />
              )}
              <span className={`whitespace-nowrap font-medium ${theme.text}`}>{buttonLabel}</span>
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5">
              <ChevronUpDownIcon className={`h-4 w-4 ${theme.faint}`} aria-hidden="true" />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options
              className={`absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-md border py-1 text-xs shadow-lg focus:outline-none ${theme.border} ${theme.surface}`}
            >
              <Listbox.Option value="all" as={Fragment}>
                {({ active, selected }) => (
                  <li
                    className={`relative cursor-pointer select-none px-3 py-2 ${
                      active ? theme.selectedRow : ''
                    }`}
                  >
                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'} ${theme.text}`}>
                      Todas as conversas
                    </span>
                    {selected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600">
                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                      </span>
                    )}
                  </li>
                )}
              </Listbox.Option>

              <Listbox.Option value="mine" as={Fragment}>
                {({ active, selected }) => (
                  <li
                    className={`relative cursor-pointer select-none px-3 py-2 ${
                      active ? theme.selectedRow : ''
                    }`}
                  >
                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'} ${theme.text}`}>
                      Minhas conversas
                    </span>
                    {selected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600">
                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                      </span>
                    )}
                  </li>
                )}
              </Listbox.Option>

              {groups.length > 0 && (
                <>
                  <div className={`my-1 border-t ${theme.border}`} />
                  <p className={`px-3 pb-1 text-[10px] font-bold uppercase tracking-wide ${theme.faint}`}>
                    Grupos de atendimento
                  </p>

                  {groups.map((group) => (
                    <Listbox.Option
                      key={group.supportGroupId}
                      value={`group-${group.supportGroupId}` as OptionKey}
                      as={Fragment}
                    >
                      {({ active, selected }) => (
                        <li
                          className={`relative cursor-pointer select-none px-3 py-2 ${
                            active ? theme.selectedRow : ''
                          }`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <span
                              className="h-2 w-2 flex-shrink-0 rounded-full"
                              style={{ backgroundColor: group.color || '#9ca3af' }}
                              aria-hidden="true"
                            />
                            <span className={`flex-1 truncate ${selected ? 'font-semibold' : 'font-normal'} ${theme.text}`}>
                              {group.name}
                            </span>
                            <span className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${theme.badge}`}>
                              {group.openConversationCount}
                            </span>
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 right-7 flex items-center text-blue-600">
                              <CheckIcon className="h-4 w-4" aria-hidden="true" />
                            </span>
                          )}
                        </li>
                      )}
                    </Listbox.Option>
                  ))}
                </>
              )}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};
